import { Box, Button, Dialog, DialogActions, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogContent } from '@work4all/components';
import { SearchResultItem } from '@work4all/components/lib/input/search-result-item/SearchResultItem';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { fetchPlaceDetails, fetchPlaceSuggestions } from './places-api';
import {
  NewBusinessPartnerTemplate,
  prepareBusinessPartnerTemplate,
} from './places-utils';

export function PlaceSuggestions(props: {
  query: string;
  disabled?: boolean;
  entity: Entities.customer | Entities.supplier;
  onSelect: (template: NewBusinessPartnerTemplate) => void;
}) {
  const { entity, query, disabled, onSelect } = props;

  const { t } = useTranslation();

  const queryTrimmed = query.trim();
  const isQueryEmpty = queryTrimmed === '';

  const isActive = !disabled && !isQueryEmpty;

  const [suggestions, setSuggestions] = useState<
    google.maps.places.PlaceResult[]
  >([]);

  const attributionsElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive) {
      setSuggestions([]);
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      const controller = new AbortController();
      const signal = controller.signal;

      fetchPlaceSuggestions({
        query: queryTrimmed,
        // Instructs the Place Autocomplete service to return only business
        // results.
        type: 'establishment',
        container: attributionsElementRef.current,
        signal,
      }).then(
        (suggestions) => {
          // Limit suggestions to top 5 results.
          setSuggestions(suggestions.slice(0, 5));
        },
        (reason) => {
          if (reason instanceof Error && reason.name === 'AbortError') {
            return;
          }

          setSuggestions([]);

          throw reason;
        }
      );

      return () => {
        controller.abort();
      };
    }
  }, [isActive, queryTrimmed]);

  const attributions = useMemo(() => {
    return suggestions
      .flatMap((suggestion) => suggestion.html_attributions)
      .filter(Boolean);
  }, [suggestions]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<google.maps.places.PlaceResult>(null);

  const handleSelection = useCallback(async () => {
    const details = await fetchPlaceDetails({
      placeId: selectedSuggestion.place_id,
      container: attributionsElementRef.current,
    });

    const template = prepareBusinessPartnerTemplate(details);

    setDialogOpen(true);

    onSelect(template);
  }, [onSelect, selectedSuggestion?.place_id]);

  if (disabled) {
    return null;
  }

  const createNewKey =
    entity === Entities.customer
      ? 'CUSTOMER.NEW_CUSTOMER'
      : 'SUPPLIER.NEW_SUPPLIER';
  const createNewText = t(createNewKey);

  return (
    <div>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('ASSIGN_INCOMING_EMAILS.LINK_MAILBOX')}
      >
        <DialogContent>
          <Typography>
            {t('SUGGESTION.CREATE_BUSINESSPARNTER_CONFIRMATION')}
          </Typography>
          <br />
          <Typography variant="h4">{selectedSuggestion?.name}</Typography>
          <Typography>{selectedSuggestion?.formatted_address}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            size="large"
            color="primary"
            onClick={() => setDialogOpen(false)}
          >
            {t('COMMON.NO')}
          </Button>
          <Button
            size="large"
            color="primary"
            onClick={() => {
              setDialogOpen(false);
              handleSelection();
            }}
          >
            {t('COMMON.YES')}
          </Button>
        </DialogActions>
      </Dialog>

      {isActive && (
        <Typography variant="body1" sx={{ paddingX: 2, paddingY: 1 }}>
          {t('FILE.SEARCH.NOTHING_FOUND')}
        </Typography>
      )}

      {suggestions.length > 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              paddingX: 2,
            }}
          >
            <Divider textAlign="left" sx={{ paddingY: 1, flex: 1 }}>
              <span>{createNewText}</span>
            </Divider>

            <img src="/assets/img/google_on_white.png" alt="Google" />
          </Box>

          <div>
            {suggestions.map((suggestion) => {
              return (
                <SearchResultItem
                  key={suggestion.place_id}
                  id={undefined}
                  title={
                    <Typography component="span">
                      {suggestion.name}, {suggestion.formatted_address}
                    </Typography>
                  }
                  onClick={async () => {
                    setSelectedSuggestion(suggestion);
                    setDialogOpen(true);
                  }}
                />
              );
            })}
          </div>
        </>
      )}

      <div ref={attributionsElementRef}>
        {attributions.map((attribution, index) => {
          // All html_attributions returned from Places API must be displayed on
          // the page.
          return (
            <div
              key={index}
              dangerouslySetInnerHTML={{ __html: attribution }}
            />
          );
        })}
      </div>
    </div>
  );
}
